import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Booking } from "@/types/domain";
import { usePendingBookings } from "@/hooks/usePendingBookings";
import { BookingPopupModal } from "@/features/bookings/BookingPopupModal";
import { useAppStore } from "@/store/app-store";
import { useBookingPopupStore } from "@/store/booking-popup-store";
import { isBookingResponseWindowOpen } from "@/lib/booking-window";
import { playAlertSound } from "@/lib/notification-sound";
import { StayAuditPrompt } from "@/features/notifications/StayAuditPrompt";

export function NotificationProvider({ children }: { children: ReactNode }) {
  const isOwner = useAppStore((state) => state.user?.role === "OWNER");
  const hasSession = useAppStore((state) => Boolean(state.token));
  const pendingBookings = usePendingBookings();
  const dismissedIds = useBookingPopupStore((state) => state.dismissedIds);
  const dismiss = useBookingPopupStore((state) => state.dismiss);
  const knownIds = useRef<Set<string>>(new Set());
  const seeded = useRef(false);
  const [now, setNow] = useState(Date.now());
  const openBookings = (pendingBookings.data ?? []).filter((booking) => booking.bookingStatus === "PENDING" && isBookingResponseWindowOpen(booking, now));
  const pending = openBookings.filter((booking) => !dismissedIds.includes(booking.id));
  const activeBooking = pending[0] as Booking | undefined;

  useEffect(() => {
    if (!openBookings.length) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [openBookings.length]);

  useEffect(() => {
    if (!pendingBookings.data) return;
    const currentIds = new Set(pendingBookings.data.map((booking) => booking.id));
    const hasNewBooking = Array.from(currentIds).some((id) => !knownIds.current.has(id));
    // The first load only seeds what already existed; after that any new id rings - even when the
    // list was empty before (checking knownIds.size kept the first booking of a quiet spell silent).
    if (hasNewBooking && seeded.current) playAlertSound();
    knownIds.current = currentIds;
    seeded.current = true;
  }, [pendingBookings.data]);

  return (
    <>
      {children}
      {hasSession && <StayAuditPrompt />}
      {isOwner && activeBooking && <BookingPopupModal booking={activeBooking} onClose={() => dismiss(activeBooking.id)} />}
    </>
  );
}
